import { useEffect, useRef } from 'react'

import type { QueueSnapshot, QueuedWrite } from './types.js'

/**
 * Acting on a write **once the server has actually taken it**, rather than once it is queued.
 *
 * `OFF-001a` puts every write through the queue, and {@link WriteQueue.enqueue} resolves as soon as
 * the write is persisted and the snapshot emitted: the transport runs later, in the drain loop. So
 * a screen that calls its parent's callback on the line after `await enqueue(...)` is telling the
 * parent to re-read **before the POST has been sent**, and the read that follows returns the state
 * from before the write.
 *
 * That was not a theory. Four screens did exactly this, and a browser walkthrough against a real
 * server found all four (`PROGRESS.md` problem 111):
 *
 * - taking PKR 5,000 left the order detail reading "No payments have been taken yet" and the full
 *   balance still owing, which invites a second collection of money already in the drawer
 * - opening a drawer left the shell's banner reading "Drawer closed" and `TakePayment` refusing
 *   cash into the session it had just opened
 * - a job transition left the old status on screen with the same button offered again
 * - counting and closing left the drawer showing as open
 *
 * Each one corrected itself on leaving the section and coming back, because the read effects are
 * keyed on the section, and nothing on screen said so.
 *
 * **Keyed on the write's own id, not on "some write is sent".** `#update` emits on every status
 * change and `decisions/0005` keeps a sent write for the idempotency retention window, so an effect
 * that fires whenever a sent write exists would re-fire on every emit for a week of retention. The
 * announced set below makes it once per write, and the id comes from the caller so one screen's
 * write cannot trigger another's refresh.
 */

/** The write, if it is this one and the server has taken it. `undefined` in every other state. */
export function settledWrite(
  snapshot: QueueSnapshot,
  writeId: string | null | undefined,
): QueuedWrite | undefined {
  if (writeId === null || writeId === undefined || writeId === '') return undefined
  const write = snapshot.writes.find((candidate) => candidate.id === writeId)
  return write?.status === 'sent' ? write : undefined
}

/**
 * Call `onSettled` the first time this write reaches `sent`, with the write and the server's answer
 * on it. Never called for a pending, sending or blocked write, and never called twice for the same
 * write.
 */
export function useWriteSettled(
  snapshot: QueueSnapshot,
  writeId: string | null | undefined,
  onSettled: (write: QueuedWrite) => void,
): void {
  const announced = useRef<Set<string>>(new Set())
  const latest = useRef(onSettled)

  // Declared before the effect below so the assignment lands first on every render, which is what
  // lets the callback close over current state without being a dependency of the effect that fires
  // it. A callback in the dependency list would re-run the effect on every parent render.
  useEffect(() => {
    latest.current = onSettled
  })

  useEffect(() => {
    const write = settledWrite(snapshot, writeId)
    if (write === undefined) return
    if (announced.current.has(write.id)) return
    announced.current.add(write.id)
    latest.current(write)
  }, [snapshot, writeId])
}
